/**
 * Novo cliente (DEMO-03, telas/NovoCliente.tsx), agora persistente: Grupo de
 * Cliente + entidades legais (a 1ª é a PRINCIPAL; Matriz/Filial/Unidade é atributo) +
 * contatos, gravados numa única transação auditada.
 * CEP: consulta automática ao completar 8 dígitos (ViaCEP, BrasilAPI de reserva;
 * decisão de 07/10/2026) — sugestão editável, nada gravado pela consulta.
 * CNPJ: consulta automática na Receita ao completar um CNPJ válido (BrasilAPI;
 * decisão de 07/10/2026) — sugestão editável com alerta de situação cadastral,
 * nada gravado; IE manual. Validado (dígitos verificadores) e conferido contra
 * duplicidade no HUB.
 * Faturamento e entrega não ficam aqui: pertencem à Proposta.
 */
import { useState } from 'react';
import { api } from '../api.ts';
import { useAmbiente } from '../ambiente.tsx';
import { useSessao } from '../sessao.tsx';
import { navegar } from '../rotas.ts';
import { EstadoCarga, useCarga } from './ui.tsx';
import { CampoPapel } from './Contatos.tsx';
import { CamposEntidade, CamposGrupo, CamposPerfil, entidadeVazia, perfilVazio, type FormGrupo } from './Clientes.tsx';
import { ROTULO_TIPO_ENTIDADE, TIPOS_ENTIDADE, tipoSugerido, type Cliente, type Indicacao, type Opcoes } from './tipos.ts';

type FormEntidade = ReturnType<typeof entidadeVazia>;
interface FormContato {
  chave: number;
  nome: string;
  cargo: string;
  departamento: string;
  email: string;
  telefone: string;
  whatsapp: string;
  papel: string;
  entidade_indice: number;
  status: 'ativo' | 'inativo';
  principal: boolean;
  observacao: string;
}
let seq = 0;
const novoContato = (principal: boolean): FormContato => ({ chave: ++seq, nome: '', cargo: '', departamento: '', email: '', telefone: '', whatsapp: '', papel: '', entidade_indice: 0, status: 'ativo', principal, observacao: '' });

export function NovoCliente() {
  const { aceitarFicticio } = useAmbiente();
  const { eu } = useSessao();
  const opcoes = useCarga(() => api.get<Opcoes>('/api/clientes/opcoes'), []);
  // tipoManual: o usuário escolheu o tipo; senão ele acompanha a sugestão do CNPJ (/0001 → Matriz)
  const [ents, setEnts] = useState<(FormEntidade & { chave: number; tipoManual?: boolean })[]>(() => [{ ...entidadeVazia('matriz'), chave: ++seq }]);
  const [g, setG] = useState<FormGrupo>({ nome: '', marca: '', segmento: '', subsegmento: '', responsavel_id: eu?.id ?? '', origem: '', observacoes: '', perfil: perfilVazio() });
  const [indicacao, setIndicacao] = useState<Indicacao | undefined>();
  const [contatos, setContatos] = useState<FormContato[]>([]);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const setEnt = (i: number, patch: Partial<FormEntidade>) =>
    setEnts((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch, ...(patch.cnpj !== undefined && !x.tipoManual && patch.cnpj.replace(/\D/g, '').length === 14 ? { tipo: tipoSugerido(patch.cnpj) } : {}) } : x)));
  // a 1ª entidade é a PRINCIPAL do Cliente (0022); Matriz/Filial/Unidade é atributo de cada entidade
  const rotuloEntidade = (i: number) => (i === 0 ? 'Entidade principal' : `Entidade ${i + 1}`);

  const salvar = async () => {
    setErro('');
    if (!g.nome.trim()) return setErro('Informe o nome do Grupo de Cliente.');
    if (!g.origem) return setErro('Selecione a Origem.');
    if (g.origem === 'Indicação' && !indicacao) return setErro('Origem Indicação: informe quem indicou.');
    if (!contatos.some((k) => k.nome.trim())) return setErro('Informe pelo menos um contato.');
    setSalvando(true);
    try {
      const { perfil, ...grupo } = g;
      const c = await api.post<Cliente>('/api/clientes', {
        ...grupo,
        ...perfil,
        indicacao: g.origem === 'Indicação' && indicacao ? { tipo: indicacao.tipo, id: indicacao.id, nome: indicacao.nome } : undefined,
        entidades: ents.map(({ chave: _c, tipoManual: _m, ...e }) => e),
        contatos: contatos.filter((k) => k.nome.trim()).map(({ chave: _c, ...k }) => k),
      });
      navegar(`/clientes/${c.id}`);
    } catch (e) {
      setErro((e as Error).message);
      window.scrollTo(0, 0);
    } finally {
      setSalvando(false);
    }
  };

  const texto = (rotulo: string, valor: string, set: (v: string) => void, extra: { largo?: boolean; placeholder?: string; teste?: string } = {}) => (
    <label key={rotulo} className={extra.largo ? 'largura-total' : undefined}>
      {rotulo}
      <input value={valor} onChange={(e) => set(e.target.value)} placeholder={extra.placeholder} data-teste={extra.teste} />
    </label>
  );

  if (!opcoes.dados) return <EstadoCarga carregando={opcoes.carregando} erro={opcoes.erro} recarregar={opcoes.recarregar} teste="novo-cliente" />;
  return (
    <>
      <h1 className="titulo-pagina">Novo cliente</h1>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-novo-cliente">
          {erro}
        </p>
      )}
      <form
        className="form-cliente"
        data-teste="form-cliente"
        onSubmit={(e) => {
          e.preventDefault();
          void salvar();
        }}
      >
        <h2>Entidades legais — comece pela entidade principal (de preferência a matriz, /0001)</h2>
        {ents.map((e, n) => (
          <fieldset key={e.chave} className="form-grade entidade-form" data-teste={`entidade-form-${n}`}>
            <legend>{rotuloEntidade(n)}</legend>
            <CamposEntidade e={e} set={(p) => setEnt(n, p)} cnpjEditavel prefixo={`entidade-${n}`} aceitarFicticio={aceitarFicticio} />
            <label>
              Tipo
              <select value={e.tipo} onChange={(x) => setEnts((xs) => xs.map((y, j) => (j === n ? { ...y, tipo: x.target.value as FormEntidade['tipo'], tipoManual: true } : y)))} data-teste={`tipo-entidade-${n}`}>
                {TIPOS_ENTIDADE.map((t) => (
                  <option key={t} value={t}>
                    {ROTULO_TIPO_ENTIDADE[t]}
                  </option>
                ))}
              </select>
            </label>
            {n > 0 && (
              <div className="largura-total">
                <button type="button" className="botao-secundario pequeno" onClick={() => (setEnts(ents.filter((_, j) => j !== n)), setContatos(contatos.map((k) => ({ ...k, entidade_indice: k.entidade_indice === n ? 0 : k.entidade_indice > n ? k.entidade_indice - 1 : k.entidade_indice }))))}>
                  Remover entidade {n + 1}
                </button>
              </div>
            )}
          </fieldset>
        ))}
        <div className="acoes-form esquerda">
          <button type="button" className="botao-secundario" onClick={() => setEnts([...ents, { ...entidadeVazia('filial'), chave: ++seq }])} data-teste="adicionar-filial">
            Adicionar filial
          </button>
        </div>

        <h2>Grupo de Cliente e perfil</h2>
        <fieldset className="form-grade">
          <legend>Grupo de Cliente</legend>
          <CamposGrupo g={g} set={(p) => setG({ ...g, ...p })} opcoes={opcoes.dados} indicacao={indicacao} setIndicacao={setIndicacao} />
        </fieldset>
        <fieldset className="form-grade">
          <legend>Perfil para e-mail marketing</legend>
          <CamposPerfil perfil={g.perfil} set={(perfil) => setG({ ...g, perfil })} />
        </fieldset>

        <h2>Contatos</h2>
        {contatos.map((k, i) => {
          const set = (patch: Partial<FormContato>) => setContatos(contatos.map((x, j) => (j === i ? { ...x, ...patch } : patch.principal ? { ...x, principal: false } : x)));
          return (
            <fieldset key={k.chave} className="form-grade" data-teste="contato-form">
              <legend>Contato {i + 1}</legend>
              {texto('Nome', k.nome, (v) => set({ nome: v }), { teste: `contato-${i}-nome` })}
              {texto('Cargo', k.cargo, (v) => set({ cargo: v }))}
              {texto('Departamento', k.departamento, (v) => set({ departamento: v }))}
              {texto('E-mail', k.email, (v) => set({ email: v }), { placeholder: 'nome@empresa.com.br', teste: `contato-${i}-email` })}
              {texto('Telefone com DDD', k.telefone, (v) => set({ telefone: v }), { placeholder: '(00) 90000-0000' })}
              {texto('WhatsApp', k.whatsapp, (v) => set({ whatsapp: v }))}
              <CampoPapel valor={k.papel} aoMudar={(v) => set({ papel: v })} papeis={opcoes.dados?.papeis_contato ?? []} configurados={Boolean(opcoes.dados?.papeis_configurados)} teste={`contato-${i}-papel`} />
              <label>
                Matriz/filial relacionada
                <select value={k.entidade_indice} onChange={(ev) => set({ entidade_indice: Number(ev.target.value) })}>
                  {ents.map((e, n) => (
                    <option key={e.chave} value={n}>
                      {rotuloEntidade(n)} {e.cnpj && `— ${e.cnpj}`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select value={k.status} onChange={(ev) => set({ status: ev.target.value as FormContato['status'] })}>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </label>
              <label className="opcao">
                <input type="radio" name="contato-principal" checked={k.principal} onChange={() => set({ principal: true })} /> Contato principal
              </label>
              {texto('Observação', k.observacao, (v) => set({ observacao: v }), { largo: true })}
              <div className="largura-total">
                <button type="button" className="botao-secundario pequeno" onClick={() => setContatos(contatos.filter((_, j) => j !== i))}>
                  Remover contato {i + 1}
                </button>
              </div>
            </fieldset>
          );
        })}
        <div className="acoes-form">
          <button type="button" className="botao-secundario" onClick={() => setContatos([...contatos, novoContato(contatos.length === 0)])} data-teste="adicionar-contato">
            Adicionar contato
          </button>
          <a className="botao-secundario" href="#/clientes">
            Cancelar
          </a>
          <button type="submit" className="botao-primario" disabled={salvando} data-teste="salvar-novo-cliente">
            Salvar cliente
          </button>
        </div>
      </form>
    </>
  );
}
